/**
 * MarkdownContent — rendu markdown léger sans dépendance externe.
 * Supporte : gras, italique, listes à puces, listes numérotées,
 * paragraphes, code inline, titres H1-H3, liens de navigation internes.
 *
 * Liens internes : [→ Nom du module](/moduleId) → bouton chip de navigation.
 * Le prop `onNavigate(moduleId)` est requis pour activer les liens.
 */

import React from 'react';

// Transforme un bloc inline (texte, bold, italic, code inline, liens)
function renderInline(text, onNavigate) {
  const parts = [];
  // Matches : **bold**, *italic*, `code`, [text](url)
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1]) {
      parts.push(<strong key={match.index}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={match.index}>{match[4]}</em>);
    } else if (match[5]) {
      parts.push(
        <code key={match.index} className="bg-gray-100 text-gray-800 rounded px-1 py-0.5 text-xs font-mono">
          {match[6]}
        </code>
      );
    } else if (match[7]) {
      const linkText = match[8];
      const linkUrl = match[9];
      if (linkUrl.startsWith('/') && onNavigate) {
        const moduleId = linkUrl.slice(1); // retire le "/"
        parts.push(
          <button
            key={match.index}
            onClick={() => onNavigate(moduleId)}
            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition-colors mx-0.5 cursor-pointer"
          >
            {linkText}
          </button>
        );
      } else {
        // Lien externe ou pas de onNavigate → texte brut
        parts.push(linkText);
      }
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

// Détecte si une ligne est un item de liste
const BULLET_RE = /^[-•]\s+(.+)$/;
const NUMBER_RE = /^\d+\.\s+(.+)$/;
const H1_RE = /^#\s+(.+)$/;
const H2_RE = /^##\s+(.+)$/;
const H3_RE = /^###\s+(.+)$/;

export default function MarkdownContent({ content, onNavigate, className = '' }) {
  if (!content) return null;

  const lines = content.split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Ligne vide → séparateur de blocs
    if (!line.trim()) {
      i++;
      continue;
    }

    // Titres
    if (H3_RE.test(line)) {
      blocks.push(
        <h3 key={i} className="font-semibold text-gray-800 text-sm mt-3 mb-1">
          {renderInline(line.replace(H3_RE, '$1'), onNavigate)}
        </h3>
      );
      i++;
      continue;
    }
    if (H2_RE.test(line)) {
      blocks.push(
        <h3 key={i} className="font-bold text-gray-800 text-sm mt-3 mb-1">
          {renderInline(line.replace(H2_RE, '$1'), onNavigate)}
        </h3>
      );
      i++;
      continue;
    }
    if (H1_RE.test(line)) {
      blocks.push(
        <h2 key={i} className="font-bold text-gray-900 mt-3 mb-1">
          {renderInline(line.replace(H1_RE, '$1'), onNavigate)}
        </h2>
      );
      i++;
      continue;
    }

    // Liste à puces
    if (BULLET_RE.test(line)) {
      const items = [];
      while (i < lines.length && BULLET_RE.test(lines[i])) {
        items.push(<li key={i}>{renderInline(lines[i].replace(BULLET_RE, '$1'), onNavigate)}</li>);
        i++;
      }
      blocks.push(
        <ul key={`ul-${i}`} className="list-disc list-inside space-y-1 my-1.5 text-gray-700">
          {items}
        </ul>
      );
      continue;
    }

    // Liste numérotée
    if (NUMBER_RE.test(line)) {
      const items = [];
      while (i < lines.length && NUMBER_RE.test(lines[i])) {
        items.push(<li key={i}>{renderInline(lines[i].replace(NUMBER_RE, '$1'), onNavigate)}</li>);
        i++;
      }
      blocks.push(
        <ol key={`ol-${i}`} className="list-decimal list-inside space-y-1 my-1.5 text-gray-700">
          {items}
        </ol>
      );
      continue;
    }

    // Paragraphe — accumule les lignes non-vides consécutives
    const paragraphLines = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !BULLET_RE.test(lines[i]) &&
      !NUMBER_RE.test(lines[i]) &&
      !H1_RE.test(lines[i]) &&
      !H2_RE.test(lines[i]) &&
      !H3_RE.test(lines[i])
    ) {
      paragraphLines.push(lines[i]);
      i++;
    }
    if (paragraphLines.length > 0) {
      const text = paragraphLines.join(' ');
      blocks.push(
        <p key={`p-${i}`} className="my-1 text-gray-700">
          {renderInline(text, onNavigate)}
        </p>
      );
    }
  }

  return (
    <div className={`space-y-0.5 leading-relaxed ${className}`}>
      {blocks}
    </div>
  );
}
